import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const QuoteSection = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    quantity: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const serviceOptions = [
    { value: 'polyester', label: 'Polyester Fabric' },
    { value: 'natural-crepe', label: 'Natural Crepe' },
    { value: 'velvet-embroidery', label: 'Velvet Embroidery' },
    { value: 'chinon-shimmer', label: 'Chinon & Shimmer' },
    { value: 'other', label: 'Other Fabrics' }
  ];

  const quantityOptions = [
    { value: '1-100', label: '1 - 100 meters' },
    { value: '101-500', label: '101 - 500 meters' },
    { value: '501-1000', label: '501 - 1,000 meters' },
    { value: '1001-5000', label: '1,001 - 5,000 meters' },
    { value: '5000+', label: '5,000+ meters' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Mock success response
    alert('Thank you for your inquiry! We will contact you within 24 hours with a detailed quote.');
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      service: '',
      quantity: '',
      message: ''
    });

    setIsSubmitting(false);
  };

  const handleDetailedQuote = () => {
    navigate('/contact');
  };

  const features = [
    {
      icon: 'Clock',
      title: 'Quick Response',
      description: '24-hour quote turnaround'
    },
    {
      icon: 'Shield',
      title: 'Quality Guarantee',
      description: '100% satisfaction assured'
    },
    {
      icon: 'Truck',
      title: 'Free Consultation',
      description: 'Expert guidance included'
    },
    {
      icon: 'Award',
      title: 'Competitive Pricing',
      description: 'Best value for quality'
    }
  ];

  return (
    <section className="py-16 bg-gradient-heritage">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-white">
            <h2 className="text-3xl md:text-4xl font-poppins font-bold mb-6">
              Get Your Custom Quote Today
            </h2>
            <p className="text-lg text-white/80 mb-8 leading-relaxed">
              Ready to bring your textile vision to life? Get a personalized quote tailored to your specific requirements. Our experts will provide detailed pricing and recommendations for your project.
            </p>

            {/* Features Grid */}
            <div className="grid sm:grid-cols-2 gap-6 mb-8">
              {features?.map((feature, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name={feature?.icon} size={20} color="white" />
                  </div>
                  <div>
                    <div className="font-semibold text-white mb-1">{feature?.title}</div>
                    <div className="text-sm text-white/70">{feature?.description}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Contact Info */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <h3 className="font-poppins font-semibold text-white mb-4">
                Need Immediate Assistance?
              </h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Icon name="Phone" size={18} color="white" />
                  <span className="text-white/90">+91 99740 56700</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Icon name="Mail" size={18} color="white" />
                  <span className="text-white/90">info@kunjvihariimpex.com</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Icon name="MessageCircle" size={18} color="white" />
                  <span className="text-white/90">WhatsApp: +91 99740 56700</span>
                </div>
              </div>
            </div>
          </div>

          {/* Quote Form */}
          <div className="bg-background rounded-2xl shadow-heritage p-8">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-poppins font-bold text-primary mb-2">
                Quick Quote Request
              </h3>
              <p className="text-text-secondary">
                Fill out the form below and we'll get back to you within 24 hours
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <Input
                  label="Full Name"
                  type="text"
                  placeholder="Enter your name"
                  value={formData?.name}
                  onChange={(e) => handleInputChange('name', e?.target?.value)}
                  required
                />
                <Input
                  label="Email Address"
                  type="email"
                  placeholder="Enter your email"
                  value={formData?.email}
                  onChange={(e) => handleInputChange('email', e?.target?.value)}
                  required
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <Input
                  label="Phone Number"
                  type="tel"
                  placeholder="Enter your phone"
                  value={formData?.phone}
                  onChange={(e) => handleInputChange('phone', e?.target?.value)}
                  required
                />
                <Input
                  label="Company Name"
                  type="text"
                  placeholder="Enter company name"
                  value={formData?.company}
                  onChange={(e) => handleInputChange('company', e?.target?.value)}
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <Select
                  label="Service Required"
                  placeholder="Select service"
                  options={serviceOptions}
                  value={formData?.service}
                  onChange={(value) => handleInputChange('service', value)}
                  required
                />
                <Select
                  label="Estimated Quantity"
                  placeholder="Select quantity"
                  options={quantityOptions}
                  value={formData?.quantity}
                  onChange={(value) => handleInputChange('quantity', value)}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Project Details
                </label>
                <textarea
                  value={formData?.message}
                  onChange={(e) => handleInputChange('message', e?.target?.value)}
                  placeholder="Tell us about your project requirements, materials, colors, deadlines, etc."
                  className="w-full p-3 border border-border rounded-lg resize-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  rows={4}
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  type="submit"
                  variant="default"
                  size="lg"
                  fullWidth
                  className="btn-textile"
                  loading={isSubmitting}
                  iconName="Send"
                  iconPosition="left"
                  iconSize={18}
                >
                  {isSubmitting ? 'Sending...' : 'Get Quote'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  fullWidth
                  onClick={handleDetailedQuote}
                  iconName="FileText"
                  iconPosition="left"
                  iconSize={18}
                >
                  Detailed Quote
                </Button>
              </div>
            </form>

            <div className="text-center mt-6 pt-6 border-t border-border">
              <p className="text-sm text-text-secondary">
                By submitting this form, you agree to our{' '}
                <span className="text-primary cursor-pointer hover:underline">
                  Privacy Policy
                </span>{' '}
                and{' '}
                <span className="text-primary cursor-pointer hover:underline">
                  Terms of Service
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default QuoteSection;